/** Pooled projectile system — pre-allocates physics-enabled rectangles and recycles them on expiry or wall impact. */
import Phaser from 'phaser';
import { normalize, worldToTile } from '../utils/Math';
import { crispRect } from '../ui/CrispUtil';

/** Per-projectile runtime state tracked alongside its Phaser game object. */
interface ProjectileState {
  active: boolean;
  lifetimeMs: number;
  elapsed: number;
  damage: number;
  attackType: string;
  rect: Phaser.GameObjects.Rectangle;
}

/** Config passed to spawn() — matches the shape the EnemyRangedSkillEffect emits via its ProjectileSpawner callback. */
export interface ProjectileConfig {
  speed: number;
  damage: number;
  lifetimeMs: number;
  size: number;
  color: number;
  attackType: string;
}

const POOL_SIZE = 16;

/**
 * Object-pool for enemy projectiles. Pre-allocates invisible physics rectangles
 * at scene creation. spawn() activates one with a velocity toward the target.
 * update() ticks lifetimes and recycles expired/collided projectiles.
 *
 * GameScene registers a single `physics.overlap(pool.group, player.sprite, onHit)`
 * callback — projectiles never use the proximity-based checkEnemyAttackHits path.
 */
export class ProjectilePool {
  private pool: ProjectileState[] = [];
  private _group: Phaser.GameObjects.Group;
  private groundLayer: Phaser.Tilemaps.TilemapLayer;

  constructor(scene: Phaser.Scene, groundLayer: Phaser.Tilemaps.TilemapLayer) {
    this.groundLayer = groundLayer;
    this._group = scene.add.group();

    for (let i = 0; i < POOL_SIZE; i++) {
      const rect = crispRect(scene, -100, -100, 6, 6, 0xffffff, 1);
      scene.physics.add.existing(rect, false);
      const body = rect.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(false);
      // Disable until spawned
      rect.setActive(false).setVisible(false);
      body.enable = false;

      this._group.add(rect);

      this.pool.push({
        active: false,
        lifetimeMs: 0,
        elapsed: 0,
        damage: 0,
        attackType: 'punch',
        rect,
      });
    }
  }

  /** The Phaser Group containing all pooled projectile objects — use for physics.overlap registration. */
  get group(): Phaser.GameObjects.Group {
    return this._group;
  }

  /**
   * Activate a pooled projectile, launching it from (x, y) toward (targetX, targetY).
   * Returns the ProjectileState for hit-callback damage lookup, or null if the pool is exhausted.
   */
  spawn(x: number, y: number, targetX: number, targetY: number, config: ProjectileConfig): ProjectileState | null {
    const slot = this.pool.find(p => !p.active);
    if (!slot) return null;

    slot.active = true;
    slot.lifetimeMs = config.lifetimeMs;
    slot.elapsed = 0;
    slot.damage = config.damage;
    slot.attackType = config.attackType;

    const rect = slot.rect;
    rect.setPosition(x, y);
    rect.setFillStyle(config.color, 1);
    rect.setActive(true).setVisible(true);

    const body = rect.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setSize(config.size, config.size);

    // Set velocity toward target
    const dir = normalize(targetX - x, targetY - y);
    body.setVelocity(dir.x * config.speed, dir.y * config.speed);

    return slot;
  }

  /** Tick all active projectiles: advance lifetime timers and recycle expired ones. */
  update(delta: number): void {
    for (const slot of this.pool) {
      if (!slot.active) continue;

      slot.elapsed += delta;
      if (slot.elapsed >= slot.lifetimeMs) {
        this.deactivate(slot);
        continue;
      }

      // Check wall collision via tilemap overlap
      const body = slot.rect.body as Phaser.Physics.Arcade.Body;
      const tile = this.groundLayer.getTileAt(worldToTile(body.center.x), worldToTile(body.center.y));
      if (tile && tile.collides) {
        this.deactivate(slot);
      }
    }
  }

  /** Look up the damage and attackType for a projectile game object (called from the physics overlap callback). */
  getDamageForRect(rect: Phaser.GameObjects.GameObject): { damage: number; attackType: string } | null {
    const slot = this.pool.find(p => p.rect === rect && p.active);
    if (!slot) return null;
    return { damage: slot.damage, attackType: slot.attackType };
  }

  /** Deactivate a projectile after it hits a target or wall. */
  deactivateByRect(rect: Phaser.GameObjects.GameObject): void {
    const slot = this.pool.find(p => p.rect === rect);
    if (slot) this.deactivate(slot);
  }

  private deactivate(slot: ProjectileState): void {
    slot.active = false;
    slot.rect.setActive(false).setVisible(false);
    slot.rect.setPosition(-100, -100);
    const body = slot.rect.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    body.setVelocity(0, 0);
  }

  /** Destroy all pooled objects and the group. */
  destroy(): void {
    for (const slot of this.pool) {
      slot.rect.destroy();
    }
    this.pool = [];
    this._group.destroy(true);
  }
}
